import { useState, useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { getDocumentActivities } from '../../api/queries_v2';

export interface DocumentActivity {
  id: string;
  verb: string;
  time: string;
  description: string;
  actor_data?: {
    id: string;
    email: string;
    full_name: string;
  };
  object_data?: {
    object_id: string;
    object_type: string;
  };
  extra?: any;
  inactive?: boolean;
}

interface UseDocumentActivitiesOptions {
  documentId: string | undefined;
  limit?: number;
  pollInterval?: number;
}

interface UseDocumentActivitiesReturn {
  activities: DocumentActivity[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
  hasMore: boolean;
  totalLoaded: number;
}

const DEFAULT_LIMIT = 100;
const DEFAULT_POLL_INTERVAL = 30000;

export const useDocumentActivities = ({
  documentId,
  limit = DEFAULT_LIMIT,
  pollInterval = DEFAULT_POLL_INTERVAL,
}: UseDocumentActivitiesOptions): UseDocumentActivitiesReturn => {
  const client = useApolloClient();
  const [activities, setActivities] = useState<DocumentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const isMountedRef = useRef(true);

  const fetchActivities = useCallback(
    async (beforeId?: string): Promise<DocumentActivity[]> => {
      if (!documentId) return [];

      try {
        const { data } = await client.query({
          query: getDocumentActivities,
          variables: {
            documentId,
            limit,
            beforeId: beforeId || null,
          },
          fetchPolicy: 'network-only',
        });

        const rawActivities = data?.getDocumentById?.documentActivities || [];

        // Map backend camelCase fields to the snake_case format the UI expects
        return rawActivities.map((a: any) => ({
          id: a.id,
          verb: a.verb,
          time: a.time,
          description: a.description,
          actor_data: a.actorData,
          object_data: a.objectData,
          extra: a.extra,
          inactive: a.inactive,
        }));
      } catch (err) {
        console.error('Error fetching document activities:', err);
        throw err;
      }
    },
    [client, documentId, limit]
  );

  const refetch = useCallback(async () => {
    if (!documentId || !isMountedRef.current) return;

    try {
      const newActivities = await fetchActivities();
      if (isMountedRef.current) {
        setActivities(newActivities);
        setHasMore(newActivities.length === limit);
        setError(null);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err as Error);
      }
    }
  }, [fetchActivities, documentId, limit]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading || activities.length === 0) return;

    setLoading(true);
    try {
      const lastId = activities[activities.length - 1].id;
      const moreActivities = await fetchActivities(lastId);
      if (isMountedRef.current) {
        const combined = [...activities, ...moreActivities];
        setActivities(combined);
        setHasMore(moreActivities.length === limit);
      }
    } catch (err) {
      console.error('Error loading more activities:', err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [activities, hasMore, loading, fetchActivities, limit]);

  // Initial load
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!documentId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const initialActivities = await fetchActivities();
        if (!cancelled && isMountedRef.current) {
          setActivities(initialActivities);
          setHasMore(initialActivities.length === limit);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled && isMountedRef.current) {
          setError(err as Error);
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [documentId, fetchActivities, limit]);

  // Polling
  useEffect(() => {
    if (!documentId || !pollInterval) return;

    const interval = setInterval(() => {
      if (isMountedRef.current) {
        refetch().catch((err) => {
          console.warn('Document activities polling error (non-critical):', err);
        });
      }
    }, pollInterval);

    return () => clearInterval(interval);
  }, [documentId, pollInterval, refetch]);

  // Immediate refetch on suggestion/comment mutations (debounced for bulk operations)
  useEffect(() => {
    if (!documentId) return;

    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleActivityChanged = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (isMountedRef.current) {
          refetch().catch((err) => {
            console.warn('Document activities event-driven refetch error (non-critical):', err);
          });
        }
      }, 1000);
    };

    window.addEventListener('document-activity-changed', handleActivityChanged);

    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('document-activity-changed', handleActivityChanged);
    };
  }, [documentId, refetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    activities,
    loading,
    error,
    refetch,
    loadMore,
    hasMore,
    totalLoaded: activities.length,
  };
};
